/**
 * Node customizado para chamadas de webhook/API externa
 * Design com ícone de link e método HTTP
 * Inclui toolbar flutuante ao selecionar
 */

import { Handle, Position, NodeProps } from '@xyflow/react';
import { Webhook, ShieldAlert } from 'lucide-react';
import { FlowNodeData, WebhookConfig } from '@/types/flows';
import { FlowNodeToolbar } from './FlowNodeToolbar';

/**
 * Componente visual do node de webhook
 */
export function WebhookNode({ data, id, selected }: NodeProps) {
  const nodeData = data as FlowNodeData;
  const config = nodeData.config as WebhookConfig | undefined;
  const url = config?.url || 'URL não configurada';
  const method = config?.method || 'POST';
  const preview = url.length > 40 ? url.substring(0, 40) + '...' : url;
  
  // Verifica se tratamento de erro está configurado
  const hasErrorHandling = config?.error_handling?.enabled || false;
  
  // Callbacks injetados via FlowCanvas
  const onEdit = (nodeData as any).onEdit;
  const onDuplicate = (nodeData as any).onDuplicate;
  const onDelete = (nodeData as any).onDelete;
  const onTestFromHere = (nodeData as any).onTestFromHere;
  
  // Cor do badge baseado no método HTTP
  const methodColor = {
    'GET': 'bg-blue-500/10 text-blue-700 border-blue-200',
    'POST': 'bg-green-500/10 text-green-700 border-green-200',
    'PUT': 'bg-orange-500/10 text-orange-700 border-orange-200',
    'DELETE': 'bg-red-500/10 text-red-700 border-red-200',
  }[method] || 'bg-gray-500/10 text-gray-700 border-gray-200';

  return (
    <div className="bg-card rounded-lg shadow-lg border-2 border-rose-500 min-w-[220px] max-w-[260px]">
      {/* Toolbar flutuante */}
      <FlowNodeToolbar
        nodeId={id}
        nodeType={nodeData.type}
        onEdit={onEdit}
        onDuplicate={onDuplicate}
        onDelete={onDelete}
        onTestFromHere={onTestFromHere}
        isVisible={selected}
      />
      
      <Handle
        type="target"
        position={Position.Top}
        className="!bg-rose-500 !w-3 !h-3"
      />
      
      <div className="p-3">
        <div className="flex items-center gap-2 mb-2">
          <div className="bg-rose-500/10 text-rose-600 p-2 rounded">
            <Webhook className="h-4 w-4" />
          </div>
          <div className="flex-1">
            <div className="font-semibold text-sm text-foreground">
              {nodeData.config?.nickname || 'Webhook'}
            </div>
          </div>
          {/* Indicador de tratamento de erro */}
          {hasErrorHandling && (
            <div className="bg-red-500/10 text-red-600 p-1 rounded" title="Tratamento de erro configurado">
              <ShieldAlert className="h-3 w-3" />
            </div>
          )}
          <div className={`text-xs font-mono px-2 py-1 rounded border ${methodColor}`}>
            {method}
          </div>
        </div>
        
        <div className="text-xs text-muted-foreground bg-muted/50 rounded p-2 font-mono break-all">
          {preview}
        </div>
      </div>
      
      <Handle
        type="source"
        position={Position.Bottom}
        className="!bg-rose-500 !w-3 !h-3"
      />
    </div>
  );
}
